import { getStorage, ref, getDownloadURL, uploadBytes, listAll } from 'firebase/storage';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

class AudioService {
  constructor() {
    try {
      console.log('=== AUDIO SERVICE INITIALIZATION START ===');

      this.storage = getStorage();
      console.log('Storage instance created:', {
        exists: !!this.storage,
        bucket: this.storage?.app?.options?.storageBucket,
        config: this.storage?.app?.options
      });

      if (!this.storage) {
        throw new Error('Failed to initialize storage');
      }

      this.audio = null;

      // Initialize speech synthesis with better mobile support
      this.initSpeechSynthesis();

      this.speaking = false;
      this.currentUtterance = null;
      this.isPlaying = false;
      this.currentDay = null;

      // Force immediate audio verification
      this.verifyTodaysAudio().then(exists => {
        console.log('Audio verification completed:', {
          exists,
          timestamp: new Date().toISOString(),
          storage: !!this.storage,
          bucket: this.storage?.app?.options?.storageBucket
        });
      }).catch(err => {
        console.error('Audio verification failed:', {
          error: err.message,
          code: err.code,
          stack: err.stack
        });
      });

      // Debug: List all audio files
      this.listAudioFiles().then(files => {
        console.log('Audio files listing completed');
      }).catch(err => {
        console.error('Error listing audio files:', {
          error: err.message,
          code: err.code,
          stack: err.stack
        });
      });

      console.log('=== AUDIO SERVICE INITIALIZATION COMPLETE ===');
    } catch (error) {
      console.error('Error initializing AudioService:', {
        error: error.message,
        code: error.code,
        stack: error.stack
      });
      throw error; // Re-throw to prevent silent failures
    }
  }

  initSpeechSynthesis() {
    if (typeof window === 'undefined') return;

    // Try to get speechSynthesis from window or webkit
    this.speechSynthesis = window.speechSynthesis ||
      window.webkitSpeechSynthesis ||
      window.mozSpeechSynthesis ||
      window.msSpeechSynthesis;

    if (this.speechSynthesis) {
      console.log('Speech synthesis available');
      this.loadVoices(); // Pre-load voices
    } else {
      console.warn('Speech synthesis not available on this device');
    }

    // Handle iOS/Safari specific initialization
    if (typeof window !== 'undefined') {
      document.addEventListener('touchstart', () => {
        if (this.speechSynthesis && !this.speaking) {
          // Create and immediately cancel an utterance to initialize
          const utterance = new SpeechSynthesisUtterance('');
          this.speechSynthesis.speak(utterance);
          this.speechSynthesis.cancel();
        }
      }, { once: true });
    }
  }

  loadVoices() {
    return new Promise((resolve) => {
      let voices = this.speechSynthesis.getVoices();

      if (voices.length > 0) {
        console.log('Voices loaded immediately:', voices.length);
        resolve(voices);
      } else {
        // Handle Chrome's async voice loading
        this.speechSynthesis.onvoiceschanged = () => {
          voices = this.speechSynthesis.getVoices();
          console.log('Voices loaded after change:', voices.length);
          resolve(voices);
        };

        // Fallback timeout
        setTimeout(() => {
          voices = this.speechSynthesis.getVoices();
          if (voices.length === 0) {
            console.warn('No voices loaded after timeout');
          }
          resolve(voices);
        }, 1000);
      }
    });
  }

  /**
   * Get the audio URL for a specific devotional
   * @param {number} day - Day of the year
   * @param {Date} [dateObj] - Optional specific date object to use for year
   * @returns {Promise<{type: string, url: string|null}>} - Audio URL info
   */
  async getAudioUrl(day, dateObj) {
    try {
      console.warn('=== AUDIO LOOKUP STARTED ===');
      console.warn('Looking up audio for day:', day, 'Date:', dateObj);

      if (!this.storage) {
        console.error('Storage not initialized');
        return { type: 'tts', url: null };
      }

      // Calculate date from day of year
      let date;
      if (dateObj) {
        date = new Date(dateObj);
      } else {
        // Fallback to 2025 default if no date provided
        date = new Date(2025, 0, 1);
        date.setDate(date.getDate() + day - 1);
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const dayOfMonth = String(date.getDate()).padStart(2, '0');

      // Try filenames - prioritize the specific year format
      const fileNames = [
        `${year}-${month}-${dayOfMonth}.mp3`,  // YYYY-MM-DD.mp3 (e.g., 2026-01-01.mp3)
        `day_${day}.mp3`                       // day_X.mp3
      ];

      // If we're not in 2025, also check the 2025 format as a fallback if desired?
      // For now, let's stick to the specific year requested. 
      // User specifically asked for 2026 files to work.

      // If the dateObj wasn't passed, we might still want to check 2025 explicit format just in case
      if (!dateObj && year !== 2025) {
        fileNames.push(`2025-${month}-${dayOfMonth}.mp3`);
      }

      console.warn('Attempting to access files:', {
        day,
        date: date.toISOString(),
        fileNames
      });

      for (const fileName of fileNames) {
        const filePath = `audio/${fileName}`;
        console.warn('Checking path:', filePath);

        try {
          const storageRef = ref(this.storage, filePath);
          const url = await getDownloadURL(storageRef);
          console.warn('Successfully found audio file:', {
            fileName,
            url,
            path: filePath
          });
          return { type: 'mp3', url };
        } catch (error) {
          console.warn(`File ${fileName} not found or inaccessible:`, {
            fileName,
            filePath,
            errorCode: error.code,
            errorMessage: error.message,
          });
          // Continue to next filename
        }
      }

      console.warn('=== NO AUDIO FILES FOUND ===');
      return { type: 'tts', url: null };
    } catch (error) {
      console.error('Error in getAudioUrl:', {
        error: error.message,
        code: error.code,
        stack: error.stack
      });
      return { type: 'tts', url: null };
    }
  }

  /**
   * Upload new audio file for a devotional
   * @param {number} day - Day of the year
   * @param {File} audioFile - The audio file to upload
   * @returns {Promise<string>} - Storage path
   */
  async uploadAudio(day, audioFile) {
    try {
      // Get date string in YYYY-MM-DD format
      const currentYear = new Date().getFullYear();
      const date = new Date(currentYear, 0, 1);
      date.setDate(date.getDate() + day - 1);
      const dateString = date.toISOString().split('T')[0];

      const storagePath = `audio/${dateString}.mp3`;
      const storageRef = ref(this.storage, storagePath);

      // Upload the file
      await uploadBytes(storageRef, audioFile);

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Create mapping in Firestore
      await addDoc(collection(db, 'audio_files'), {
        dayOfYear: Number(day),
        dateString,
        downloadURL,
        fileName: `${dateString}.mp3`,
        uploadedAt: new Date(),
        fileSize: audioFile.size
      });

      return storagePath;
    } catch (error) {
      console.error('Error uploading audio:', error);
      throw error;
    }
  }

  async speakText(text) {
    if (!this.speechSynthesis) {
      console.error('Speech synthesis not available');
      throw new Error('Text-to-speech is not supported on this device');
    }

    if (this.speaking) {
      this.stopSpeaking();
    }

    try {
      const voices = await this.loadVoices();
      const utterance = new SpeechSynthesisUtterance(text);

      // Optimize settings for better mobile performance
      utterance.rate = 0.9;
      utterance.pitch = 1;
      utterance.volume = 1;

      // Find the best available voice
      const preferredVoice = voices.find(voice =>
        (voice.name.includes('Natural') ||
          voice.name.includes('Premium') ||
          voice.name.includes('Enhanced')) &&
        voice.lang.startsWith('en')
      ) || voices.find(voice =>
        voice.lang === 'en-US' ||
        voice.lang === 'en-GB'
      ) || voices[0];

      if (preferredVoice) {
        console.log('Using voice:', preferredVoice.name);
        utterance.voice = preferredVoice;
        utterance.lang = preferredVoice.lang;
      }

      this.currentUtterance = utterance;
      this.speaking = true;

      // Handle speech events
      utterance.onstart = () => {
        console.log('Speech started');
      };

      utterance.onend = () => {
        console.log('Speech ended');
        this.speaking = false;
        this.currentUtterance = null;
      };

      utterance.onerror = (event) => {
        console.error('Speech error:', event);
        // Handle iOS-specific error
        if (event.error === 'not-allowed' && /iPhone|iPad|iPod/.test(navigator.userAgent)) {
          console.log('iOS speech error - attempting recovery');
          setTimeout(() => this.speakText(text), 100);
        } else {
          this.speaking = false;
          this.currentUtterance = null;
          throw new Error(`Speech synthesis error: ${event.error}`);
        }
      };

      // Split long text into smaller chunks for better mobile handling
      const chunks = this.chunkText(text);
      for (const chunk of chunks) {
        utterance.text = chunk;
        await new Promise((resolve, reject) => {
          utterance.onend = resolve;
          utterance.onerror = reject;
          this.speechSynthesis.speak(utterance);
        });
      }
    } catch (error) {
      console.error('Error in speech synthesis:', error);
      this.speaking = false;
      this.currentUtterance = null;
      throw error;
    }
  }

  chunkText(text) {
    // Split text into smaller chunks (around 200 characters) at sentence boundaries
    const chunks = [];
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
    let currentChunk = '';

    for (const sentence of sentences) {
      if (currentChunk.length + sentence.length > 200) {
        chunks.push(currentChunk.trim());
        currentChunk = sentence;
      } else {
        currentChunk += sentence;
      }
    }
    if (currentChunk) {
      chunks.push(currentChunk.trim());
    }
    return chunks;
  }

  stopSpeaking() {
    if (this.speaking && this.speechSynthesis) {
      console.warn('Stopping speech synthesis');
      this.speechSynthesis.cancel();
      this.speaking = false;
      this.currentUtterance = null;
    }
  }

  pauseSpeaking() {
    if (this.speaking && this.speechSynthesis) {
      this.speechSynthesis.pause();
    }
  }

  resumeSpeaking() {
    if (this.currentUtterance && this.speechSynthesis) {
      this.speechSynthesis.resume();
    }
  }

  async play(audioInfo, fallbackText) {
    try {
      if (audioInfo?.type === 'mp3' && audioInfo?.url) {
        console.warn('Playing MP3:', audioInfo.url);

        // Stop any existing audio
        if (this.audio) {
          this.audio.pause();
          this.audio = null;
        }

        // Create and play new audio
        this.audio = new Audio(audioInfo.url);
        await this.audio.play();
        this.isPlaying = true;
        return true;
      } else if (fallbackText) {
        console.warn('No MP3 available, using TTS');
        await this.speakText(fallbackText);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error playing audio:', error);
      if (fallbackText) {
        try {
          await this.speakText(fallbackText);
          return true;
        } catch (ttsError) {
          console.error('TTS fallback also failed:', ttsError);
        }
      }
      return false;
    }
  }

  async pause() {
    if (this.audio && this.isPlaying) {
      this.audio.pause();
      this.isPlaying = false;
    }
  }

  stop() {
    console.warn('Stopping audio playback');
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
      this.isPlaying = false;
    }
    this.stopSpeaking();
  }

  async rewind() {
    if (this.audio) {
      this.audio.currentTime = 0;
    }
  }

  isCurrentlyPlaying() {
    return this.isPlaying;
  }

  getCurrentDay() {
    return this.currentDay;
  }

  getCurrentTime() {
    return this.audio ? this.audio.currentTime : 0;
  }

  getDuration() {
    return this.audio ? this.audio.duration : 0;
  }

  // Debug function to list all audio files
  async listAudioFiles() {
    try {
      console.log('=== LISTING ALL AUDIO FILES IN STORAGE ===');
      const audioRef = ref(this.storage, 'audio');

      // List all items in the audio directory
      const result = await listAll(audioRef);

      console.log('Found audio files:', {
        fileCount: result.items.length,
        files: result.items.map(item => item.name),
        prefix: audioRef.fullPath,
        bucket: this.storage.app.options.storageBucket
      });

      // Try to get URLs for each file
      for (const item of result.items) {
        try {
          const url = await getDownloadURL(item);
          console.log(`File ${item.name} is accessible at:`, url);
        } catch (urlError) {
          console.error(`Cannot access file ${item.name}:`, urlError.code);
        }
      }

      console.log('=== END AUDIO FILES LIST ===');
    } catch (error) {
      console.error('Error listing audio files:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
    }
  }

  // Verify today's audio file exists
  async verifyTodaysAudio() {
    try {
      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const fileName = `2025-${month}-${day}.mp3`;
      const filePath = `audio/${fileName}`;

      console.log('=== VERIFYING TODAY\'S AUDIO ===');
      console.log('Looking for:', {
        filePath,
        fileName,
        fullGsPath: `gs://proverbs-daily-app-data.firebasestorage.app/${filePath}`
      });

      try {
        const storageRef = ref(this.storage, filePath);
        const url = await getDownloadURL(storageRef);
        console.log('✅ Today\'s audio file exists:', {
          path: filePath,
          url: url
        });
        return true;
      } catch (error) {
        console.error('❌ Today\'s audio file not found:', {
          path: filePath,
          error: error.code
        });
        return false;
      }
    } catch (error) {
      console.error('Error verifying audio:', error);
      return false;
    }
  }
}

// Create a singleton instance
const audioService = new AudioService();

// Export the singleton instance
export { audioService };
export default audioService; 